import PropTypes from 'prop-types';
import {useEffect, useReducer, useState} from 'react';
import {useDispatch} from 'react-redux';
import {
  Container,
  Button,
  Icon,
  Loader,
  Menu,
  Tab,
  Header,
  Divider,
  Popup,
} from 'semantic-ui-react';

import {DrupalSiteStatus} from 'helpers/enums';
import httpClient from 'httpClient';
import {addError, addSuccessMessage} from 'state/actions';

import Matomo from '../../Common/Matomo';
import {drupalSubdomain, updateMatomoRegistration} from '../../Common/sharedElements';
import EnvironmentCreation from '../EnvironmentCreation/EnvironmentCreation';

import Advanced from './Advanced/Advanced';
import Aliases from './Aliases/Aliases';
import Backup from './Backup/Backup';
import Info from './Info/Info';

import styles from './Environments.module.scss';

function environmentsReducer(state, action) {
  switch (action.type) {
    case 'FETCH_DATA':
      return {
        ...state,
        isLoading: true,
      };
    case 'SET_DATA':
      return {
        ...state,
        drupalSites: action.data.drupalSites,
        isLoading: false,
      };
    case 'ENV_CREATED':
      return {
        ...state,
        drupalSites: action.data.drupalSites,
        isLoading: false,
        activeIndex:
          Object.values(action.data.drupalSites).findIndex(
            p => p.siteUrl[0] === `${action.data.envName}${drupalSubdomain}`
          ) + 1,
      };
    case 'SET_ACTIVE_TAB':
      return {
        ...state,
        activeIndex: action.data.activeIndex,
      };
    case 'SET_ACTIVE_ENV_TAB':
      return {
        ...state,
        activeTabIndex: action.data.activeTabIndex,
      };
    default:
      throw new Error();
  }
}

const Environments = ({projectName, onDrupalSites}) => {
  const initialState = {
    isLoading: false,
    // a dictionary of drupalsites(environments) resources in the current drupal project(site)
    drupalSites: {},
    // the current active vertical tab index (active environment)
    activeIndex: 1,
    // the current active horizontal tab index
    activeTabIndex: 0,
  };

  // [HOTFIX] To remove on https://gitlab.cern.ch/webservices/webservices-portal/-/merge_requests/100
  const [envsError, setEnvsError] = useState(false);

  const [
    {isLoading: isFetchingEnvs, drupalSites, activeIndex, activeTabIndex},
    dispatch,
  ] = useReducer(environmentsReducer, initialState);

  const dispatchMessage = useDispatch();

  const getDrupalSites = async projectName => {
    dispatch({
      type: 'FETCH_DATA',
    });

    try {
      const {
        data: {drupalsites},
      } = await httpClient.get(`/api/drupal/${projectName}/drupalsites`);
      await onDrupalSites(drupalsites);
      return drupalsites;
    } catch (err) {
      setEnvsError(true);
      console.error(err);
    }
  };

  const createEnvironment = async envData => {
    try {
      if (envData.cloneFrom === 'None') {
        delete envData.cloneFrom;
        await httpClient.post(`/api/drupal/project/${projectName}/site`, envData);
        dispatchMessage(
          addSuccessMessage(`The creation process of environment ${envData.name}
        has started. The new environment will be available soon.`)
        );
      } else {
        await httpClient.post(`/api/drupal/project/${projectName}/clone`, envData);
        dispatchMessage(
          addSuccessMessage(`The clone process of environment ${envData.cloneFrom}
        has started. The cloned environment will be available soon. Please note that
        if the site is large it could take up to one hour.`)
        );
      }
      const drupalsites = await getDrupalSites(projectName);

      dispatch({
        type: 'ENV_CREATED',
        data: {drupalSites: drupalsites, envName: envData.name},
      });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    (async () => {
      const drupalSites = await getDrupalSites(projectName);

      dispatch({
        type: 'SET_DATA',
        data: {drupalSites: drupalSites ? drupalSites : {}},
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectName]);

  const emptyEnvOption = {
    text: 'None - create a new site with the CERN theme',
    value: 'None',
    version: null,
  };

  const addEnvironmentPane = [
    {
      menuItem: (
        <Button
          key="newEnvironment"
          labelPosition="left"
          className={styles.ActionButton}
          style={{marginTop: 10, marginBottom: 20}}
          icon
        >
          <Icon name="add" />
          Add Environment
        </Button>
      ),
      render: function addEnvironment() {
        return (
          <Tab.Pane attached={false} className={styles.TabContents} basic>
            <EnvironmentCreation
              create={createEnvironment}
              cloneOptions={[
                emptyEnvOption,
                ...Object.entries(drupalSites)
                  .filter(([_key, value]) => value.status === DrupalSiteStatus.OK)
                  .map(([key, value]) => ({
                    text: value.siteUrl[0],
                    value: key,
                    version: value.version.name,
                  })),
              ]}
            />
          </Tab.Pane>
        );
      },
    },
  ];

  const envPanes = [
    ...addEnvironmentPane,
    ...Object.entries(drupalSites).map(([name, drupalSite]) => ({
      menuItem: (
        <Menu.Item key={drupalSite.siteUrl[0]}>
          {drupalSite.isPrimary && (
            <Popup
              trigger={<Icon name="star" color="yellow" />}
              position="left center"
              content="This environment is the primary one"
            />
          )}
          <span>{drupalSite.siteUrl[0]}</span>
        </Menu.Item>
      ),
      render: function env() {
        return (
          <Tab
            activeIndex={activeTabIndex}
            onTabChange={(_evt, {activeTabIndex}) => {
              dispatch({
                type: 'SET_ACTIVE_ENV_TAB',
                data: {activeTabIndex: activeTabIndex},
              });
            }}
            className={styles.TabContents}
            menu={{secondary: true, pointing: true}}
            panes={[
              {
                menuItem: 'Info',
                render: function info() {
                  const primaryEnv = Object.values(drupalSites).find(site => site.isPrimary);

                  function canBePrimary(site) {
                    if (site.status === DrupalSiteStatus.INITIALIZING) {
                      return "The environment is still being initialized hence you can't set it as the primary.";
                    } else if (!primaryEnv) {
                      return true;
                    } else if (primaryEnv.configuration.databaseClass !== 'standard') {
                      return 'Environment can be marked as primary only when the current primary site is using standard database class.';
                    } else if (
                      primaryEnv.version.name.localeCompare(site.version.name, undefined, {
                        numeric: true,
                        sensitivity: 'base',
                      }) > 0
                    ) {
                      return 'Only a site with version equal or higher than the current primary site can be marked as primary.';
                    }

                    return true;
                  }

                  return (
                    <Tab.Pane attached={false} basic>
                      <Info
                        key={name}
                        projectName={projectName}
                        envName={name}
                        url={drupalSite.siteUrl[0]}
                        version={drupalSite.version.name}
                        status={drupalSite.status}
                        isPrimary={drupalSite.isPrimary}
                        canBePrimary={canBePrimary(drupalSite)}
                        clonedFrom={drupalSite.configuration.clonedFrom}
                        onSave={async values => {
                          const payload = {};

                          if (values.version) {
                            payload.version = values.version;
                          }

                          if (values.url) {
                            payload.primaryUrl = values.url;
                          }

                          if (values.isPrimary) {
                            payload.isPrimary = true;
                          }

                          await httpClient.patch(
                            `/api/drupal/${projectName}/drupalsites/${name}`,
                            payload
                          );

                          dispatchMessage(
                            addSuccessMessage(`Drupal environment ${name} has been updated.`)
                          );

                          if (values.url) {
                            dispatchMessage(
                              addSuccessMessage(
                                `The new URL will be reachable in a few seconds. Please note that it may take some time for the old URL to be available for reuse again.`
                              )
                            );

                            try {
                              await updateMatomoRegistration(
                                drupalSite.matomoSiteId,
                                values.url,
                                dispatchMessage
                              );
                              dispatchMessage(
                                addSuccessMessage(
                                  'The url of the Matomo registration has been updated'
                                )
                              );
                            } catch (err) {
                              dispatchMessage(addError(err.message));
                            }
                          }

                          const drupalSites = await getDrupalSites(projectName);
                          dispatch({
                            type: 'SET_DATA',
                            data: {drupalSites: drupalSites},
                          });
                        }}
                        onDelete={async () => {
                          const drupalSites = await getDrupalSites(projectName);

                          dispatch({
                            type: 'SET_DATA',
                            data: {drupalSites: drupalSites},
                          });

                          dispatch({
                            type: 'SET_ACTIVE_TAB',
                            data: {activeIndex: Object.keys(drupalSites).length > 0 ? 1 : 0},
                          });
                        }}
                      />
                    </Tab.Pane>
                  );
                },
              },
              {
                menuItem: 'Aliases',
                render: function aliases() {
                  return (
                    <Tab.Pane attached={false} basic>
                      <Aliases
                        key={name}
                        aliases={drupalSite.siteUrl.slice(1)}
                        onAliasCreate={async values => {
                          const payload = {};

                          if (values) {
                            payload.alias = values;
                          }
                          await httpClient.post(
                            `/api/drupal/${projectName}/drupalsites/${name}/aliases`,
                            payload
                          );

                          dispatchMessage(addSuccessMessage(`Νew alias ${values} has been added`));

                          const drupalSites = await getDrupalSites(projectName);
                          dispatch({
                            type: 'SET_DATA',
                            data: {drupalSites: drupalSites},
                          });

                          dispatch({
                            type: 'SET_ACTIVE_ENV_TAB',
                            data: {activeTabIndex: 1},
                          });
                        }}
                        onAliasRemove={async values => {
                          const payload = {};

                          if (values) {
                            payload.aliases = values;
                          }

                          await httpClient.delete(
                            `/api/drupal/${projectName}/drupalsites/${name}/aliases`,
                            {data: payload}
                          );

                          const drupalSites = await getDrupalSites(projectName);
                          dispatch({
                            type: 'SET_DATA',
                            data: {drupalSites: drupalSites},
                          });

                          dispatch({
                            type: 'SET_ACTIVE_ENV_TAB',
                            data: {activeTabIndex: 1},
                          });
                        }}
                      />
                    </Tab.Pane>
                  );
                },
              },
              {
                menuItem: 'Backup',
                render: function backupManagement() {
                  return (
                    <Tab.Pane attached={false} basic>
                      <Backup
                        key={name}
                        projectName={projectName}
                        envName={name}
                        status={drupalSite.status}
                        isPrimary={drupalSite.isPrimary}
                      />
                    </Tab.Pane>
                  );
                },
              },
              {
                menuItem: 'Web Analytics',
                render: function webAnalytics() {
                  return (
                    <Tab.Pane attached={false} basic>
                      <Matomo
                        key={name}
                        projectType="drupal"
                        projectName={projectName}
                        siteUrl={drupalSite.siteUrl[0]}
                        siteId={drupalSite.matomoSiteId}
                        siteResourceName={name}
                      />
                    </Tab.Pane>
                  );
                },
              },
              {
                menuItem: 'Advanced',
                render: function advanced() {
                  return (
                    <Tab.Pane attached={false} basic>
                      <Advanced
                        key={name}
                        projectName={projectName}
                        envName={name}
                        drupalSiteConfig={drupalSite['configuration']}
                      />
                    </Tab.Pane>
                  );
                },
              },
            ]}
          />
        );
      },
    })),
  ];

  if (envsError) {
    return null;
  }

  return (
    <Container className={styles.Environments} textAlign="center">
      <Header as="h4">Environments</Header>
      <Divider />
      {isFetchingEnvs ? (
        <Loader inline="centered" active />
      ) : (
        <Tab
          grid={{paneWidth: 11, tabWidth: 5}}
          activeIndex={activeIndex}
          menu={{fluid: true, vertical: true, tabular: true}}
          panes={envPanes}
          onTabChange={(_evt, {activeIndex}) => {
            dispatch({
              type: 'SET_ACTIVE_TAB',
              data: {activeIndex},
            });
          }}
        />
      )}
    </Container>
  );
};

Environments.propTypes = {
  projectName: PropTypes.string.isRequired,
  onDrupalSites: PropTypes.func.isRequired,
};

export default Environments;
